import React, { useState, useEffect } from 'react';
import { X, Truck, Box, Package, Shield, CheckCircle, ChevronRight, AlertCircle, Printer, Loader2, Scale, Ruler, Download } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import VolumetricBudgetMeter from '../VolumetricBudgetMeter';

const LiteShipOverlay = ({ isOpen, onClose, order = null }) => {
  const { apiCall } = useAuth();
  const { toast } = useToast();
  const [step, setStep] = useState('confirm'); // 'confirm' | 'success'
  const [insurance, setInsurance] = useState(false);
  const [loading, setLoading] = useState(false);
  const [trackingInfo, setTrackingInfo] = useState(null);
  const [orderData, setOrderData] = useState(order);
  const [dimensions, setDimensions] = useState({
    weight_kg: 0.5,
    // iter-232.G — Safer defaults: 16×12×10 (vol 1920 cm³) instead of
    // 20×15×10 (vol 3000 cm³). Most vendors don't update these and were
    // being penalised by inflated volumetric weight.
    length_cm: 16,
    breadth_cm: 12,
    height_cm: 10
  });

  // Reset state when overlay opens — check for existing AWB on the order
  useEffect(() => {
    if (isOpen) {
      // If order already has AWB generated, jump straight to success view
      const existingAwb = order?.awb || order?.tracking_number || order?.shipment?.awb;
      const existingStatus = order?.fulfillment_status || order?.status;
      const alreadyShipped = existingAwb || ['shipped', 'in_transit', 'delivered', 'awb_generated', 'picked_up'].includes((existingStatus || '').toLowerCase());
      
      if (alreadyShipped) {
        setStep('success');
        setTrackingInfo({
          awb: existingAwb || 'Pending',
          courier: order?.shipment?.provider || order?.courier_name || 'Delhivery',
          tracking_url: order?.tracking_url || order?.shipment?.tracking_url,
          label_url: order?.label_url || order?.shipment?.label_url,
          pickup_date: order?.shipment?.pickup_date || order?.pickup_date,
        });
      } else {
        setStep('confirm');
      }
      setInsurance(false);
      setDimensions({ weight_kg: 0.5, length_cm: 16, breadth_cm: 12, height_cm: 10 });
      if (order) setOrderData(order);
    }
  }, [isOpen, order]);

  if (!isOpen) return null;

  // Use provided order or fallback to a placeholder
  const displayOrder = orderData || {
    id: 'No order selected',
    order_id: '#---',
    items: [],
    customer_name: 'Customer',
    total_amount: 0
  };

  const handleGenerateLabel = async () => {
    if (!displayOrder?.id || displayOrder.id === 'No order selected') {
      toast({
        title: 'No Order Selected',
        description: 'Please select an order from the dashboard first',
        variant: 'destructive',
        duration: 4000
      });
      return;
    }

    if (dimensions.weight_kg < 0.05) {
      toast({ title: 'Invalid Weight', description: 'Minimum weight is 0.05 kg', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const response = await apiCall('/api/shipping/vendor/generate-label', {
        method: 'POST',
        body: JSON.stringify({
          order_id: displayOrder.id,
          weight_kg: parseFloat(dimensions.weight_kg),
          length_cm: parseFloat(dimensions.length_cm),
          breadth_cm: parseFloat(dimensions.breadth_cm),
          height_cm: parseFloat(dimensions.height_cm),
          insurance_opt_in: insurance
        })
      });

      const shipment = response?.shipment || {};
      setTrackingInfo({
        awb: shipment.awb || response?.awb || 'Pending',
        provider: shipment.provider || shipment.courier || '',
        label_url: shipment.label_url || null,
        manifest_url: shipment.manifest_url || null,
        estimated_cost: shipment.total_logistics_cost || shipment.estimated_cost || null,
        pickup_date: 'Tomorrow',
        pickup_time: '10 AM - 6 PM'
      });

      setStep('success');
      
      toast({
        title: 'Shipment Created!',
        description: `AWB: ${shipment.awb || 'Processing'}. Pickup scheduled.`,
        duration: 5000
      });
    } catch (error) {
      console.error('Generate label error:', error);
      toast({
        title: 'Shipping Failed',
        description: error.message || 'Could not create shipment. Please try from Pack & Ship page.',
        variant: 'destructive',
        duration: 6000
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePrintLabel = () => {
    if (trackingInfo?.label_url) {
      window.open(trackingInfo.label_url, '_blank');
    } else {
      toast({
        title: 'Label Not Available',
        description: 'Shipping label URL is not available yet. Try downloading from Pack & Ship.',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-md sm:rounded-2xl flex flex-col shadow-2xl animate-in slide-in-from-bottom-10 duration-300 overflow-hidden max-h-[90vh] overflow-y-auto">
        
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-gray-100 bg-orange-50">
          <h3 className="font-bold text-orange-900 text-lg flex items-center gap-2">
            <Truck size={20} /> Ship Order {displayOrder.order_id || displayOrder.name || '#---'}
          </h3>
          <button onClick={onClose} className="p-2 bg-white/50 rounded-full hover:bg-white text-orange-800" data-testid="ship-overlay-close">
            <X size={20} />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6">
          
          {step === 'confirm' ? (
            <div className="space-y-5">
              
              {/* Order Summary */}
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                <p className="text-xs font-bold text-gray-400 uppercase mb-2">Packing List</p>
                {displayOrder.items?.length > 0 ? (
                  <ul className="space-y-2">
                    {displayOrder.items.map((item, i) => (
                      <li key={item.id || item.name || `${item.title || "item"}-${i}`} className="flex items-center gap-2 text-sm text-gray-700 font-medium">
                        <CheckCircle size={14} className="text-green-500" /> 
                        {item.title || item.name} x {item.quantity}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500">Order items will be shipped</p>
                )}
                <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between text-xs text-gray-500">
                  <span>Ship to: {displayOrder.customer_name || displayOrder.shipping_address?.name || 'Customer'}</span>
                  <span className="font-bold text-gray-800">₹{displayOrder.total_amount?.toLocaleString() || 0}</span>
                </div>
              </div>

              {/* Package Dimensions */}
              <div>
                <label className="block text-sm font-bold text-gray-800 mb-2 flex items-center gap-1.5">
                  <Ruler size={14} /> Package Dimensions
                </label>
                {/* iter-232.G — Weight on its own row, L/W/H share row 2 */}
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Weight (kg)</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0.05"
                      value={dimensions.weight_kg}
                      onChange={(e) => setDimensions(d => ({ ...d, weight_kg: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-base focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                      data-testid="ship-weight-input"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Length (cm)</label>
                      <input
                        type="number"
                        step="1"
                        min="1"
                        value={dimensions.length_cm}
                        onChange={(e) => setDimensions(d => ({ ...d, length_cm: e.target.value }))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                        data-testid="ship-length-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Breadth (cm)</label>
                      <input
                        type="number"
                        step="1"
                        min="1"
                        value={dimensions.breadth_cm}
                        onChange={(e) => setDimensions(d => ({ ...d, breadth_cm: e.target.value }))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                        data-testid="ship-breadth-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Height (cm)</label>
                      <input
                        type="number"
                        step="1"
                        min="1"
                        value={dimensions.height_cm}
                        onChange={(e) => setDimensions(d => ({ ...d, height_cm: e.target.value }))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                        data-testid="ship-height-input"
                      />
                    </div>
                  </div>
                </div>
                {/* Volumetric Budget Meter — Lite mode: always expanded for clarity */}
                <VolumetricBudgetMeter
                  length={dimensions.length_cm}
                  width={dimensions.breadth_cm}
                  height={dimensions.height_cm}
                  weight={dimensions.weight_kg}
                  mode="fulfilment"
                  defaultExpanded
                  dataTestIdPrefix="lite-ship-volumetric"
                />
              </div>

              {/* Insurance Upsell */}
              <div 
                onClick={() => setInsurance(!insurance)}
                className={`flex items-center justify-between p-3 rounded-xl border transition-colors cursor-pointer ${
                  insurance ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
                }`}
                data-testid="ship-insurance-toggle"
              >
                <div className="flex items-center gap-3">
                  <Shield size={20} className={insurance ? "text-green-600" : "text-gray-400"} />
                  <div>
                    <p className={`text-sm font-bold ${insurance ? "text-green-800" : "text-gray-600"}`}>Secure this Shipment</p>
                    <p className="text-xs text-gray-400">Protect against damage/loss</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`text-sm font-bold ${insurance ? "text-green-700" : "text-gray-500"}`}>+ ₹25</span>
                  {insurance && <CheckCircle size={16} className="ml-auto mt-1 text-green-600" />}
                </div>
              </div>

              {/* Action Button */}
              <button 
                onClick={handleGenerateLabel}
                disabled={loading}
                className="w-full bg-orange-600 text-white font-bold py-3 rounded-xl shadow-lg shadow-orange-200 hover:bg-orange-700 disabled:bg-orange-400 active:scale-95 transition-all flex items-center justify-center gap-2"
                data-testid="ship-generate-label-btn"
              >
                {loading ? (
                  <><Loader2 className="animate-spin" size={18} /> Creating Shipment...</>
                ) : (
                  <>Generate Shipping Label <ChevronRight size={18} /></>
                )}
              </button>

            </div>
          ) : (
            // SUCCESS STATE
            <div className="text-center py-4 space-y-6 animate-in zoom-in duration-300">
              <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-2">
                <CheckCircle size={32} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Ready for Pickup!</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Courier will arrive {trackingInfo?.pickup_date} between {trackingInfo?.pickup_time}.
                </p>
                {trackingInfo?.provider && (
                  <p className="text-xs text-indigo-600 font-medium mt-1">via {trackingInfo.provider}</p>
                )}
              </div>

              <div className="bg-gray-100 p-4 rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="text-left">
                    <p className="text-xs text-gray-500 uppercase font-bold">Tracking AWB</p>
                    <p className="text-lg font-mono font-bold text-gray-800" data-testid="ship-awb-number">{trackingInfo?.awb}</p>
                  </div>
                  {trackingInfo?.label_url ? (
                    <button 
                      onClick={handlePrintLabel}
                      className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-bold shadow-sm hover:bg-gray-50 flex items-center gap-2"
                      data-testid="ship-print-label-btn"
                    >
                      <Download size={16} /> Download Label
                    </button>
                  ) : (
                    <span className="text-xs text-gray-400 bg-gray-200 px-3 py-1.5 rounded-lg">Label generating...</span>
                  )}
                </div>
                {trackingInfo?.estimated_cost && (
                  <p className="text-xs text-gray-500 mt-2 text-left">Estimated shipping: ₹{trackingInfo.estimated_cost}</p>
                )}
              </div>

              <button 
                onClick={onClose}
                className="w-full text-indigo-600 font-bold text-sm hover:underline"
                data-testid="ship-close-btn"
              >
                Close & Go to Dashboard
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default LiteShipOverlay;
